import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";

function money(n) {
  const v = n || 0;
  const sign = v < 0 ? "-" : "";
  return sign + "$" + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_STYLE = {
  matched: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending_review: "bg-amber-50 text-amber-800 border-amber-200",
  approved_for_payment: "bg-blue-50 text-blue-800 border-blue-200",
  disputed: "bg-red-50 text-red-800 border-red-200",
  paid: "bg-gray-100 text-gray-700 border-gray-300",
};

const LINE_STATUS_ICON = {
  matched: { icon: CheckCircle2, color: "text-emerald-600", label: "Matched" },
  mismatch: { icon: AlertTriangle, color: "text-red-600", label: "Mismatch" },
  not_found: { icon: HelpCircle, color: "text-gray-500", label: "Not Found" },
  unmatched: { icon: HelpCircle, color: "text-gray-500", label: "—" },
};

export default function HaulerInvoiceView() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadInv = useCallback(async () => {
    const { data } = await api.get(`/hauler-invoices/${id}`);
    setInv(data);
  }, [id]);
  useEffect(() => { loadInv(); }, [loadInv]);

  if (!inv) return <div className="p-8 text-gray-500">Loading…</div>;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/hauler-invoices/${id}/status`, { status });
      toast.success(`Marked ${status.replace(/_/g, " ")}`);
      await loadInv();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title={inv.hinv_number}
        subtitle={`${inv.hauler_name} · Invoice ${inv.invoice_number}`}
        actions={
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${STATUS_STYLE[inv.status]}`}>{inv.status.replace(/_/g, " ")}</span>
            {inv.status !== "approved_for_payment" && inv.status !== "paid" && (
              <button onClick={() => setStatus("approved_for_payment")} disabled={busy} data-testid="hinv-approve-button" className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] uppercase tracking-widest font-semibold disabled:opacity-40">Approve for Payment</button>
            )}
            {inv.status !== "disputed" && inv.status !== "paid" && (
              <button onClick={() => setStatus("disputed")} disabled={busy} data-testid="hinv-dispute-button" className="px-3 py-2 border border-red-500 text-red-600 hover:bg-red-500 hover:text-white text-[11px] uppercase tracking-widest font-semibold disabled:opacity-40">Dispute</button>
            )}
            {inv.status === "approved_for_payment" && (
              <button onClick={() => setStatus("paid")} disabled={busy} data-testid="hinv-mark-paid" className="px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white text-[11px] uppercase tracking-widest font-semibold disabled:opacity-40">Mark Paid</button>
            )}
          </div>
        }
      />
      <div className="p-8">
        <Link to="/hauler-invoices" className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4" data-testid="hinv-back">
          <ArrowLeft className="w-4 h-4" /> Back to Hauler Invoices
        </Link>

        {/* Totals */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 p-5">
            <div className="label-caps">Claimed by hauler</div>
            <div className="stat-value text-3xl mt-2 text-gray-900" data-testid="hinv-total-claimed">{money(inv.total_claimed)}</div>
          </div>
          <div className="bg-white border border-gray-200 p-5">
            <div className="label-caps">System calculated</div>
            <div className="stat-value text-3xl mt-2 text-gray-900" data-testid="hinv-total-system">{money(inv.total_system)}</div>
          </div>
          <div className={`bg-white border p-5 ${Math.abs(inv.total_delta) < 0.01 ? "border-emerald-300" : "border-red-300"}`}>
            <div className="label-caps">Delta</div>
            <div className={`stat-value text-3xl mt-2 ${Math.abs(inv.total_delta) < 0.01 ? "text-emerald-700" : inv.total_delta > 0 ? "text-red-700" : "text-amber-700"}`} data-testid="hinv-total-delta">
              {inv.total_delta > 0 ? "+" : ""}{money(inv.total_delta)}
            </div>
          </div>
          <div className="bg-white border border-gray-200 p-5">
            <div className="label-caps">Exceptions</div>
            <div className="stat-value text-3xl mt-2 text-gray-900">{inv.exceptions?.length || 0}</div>
          </div>
        </div>

        {/* Reconciliation table */}
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="hinv-reconciliation">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 label-caps">Line Status</th>
                <th className="text-left px-4 py-2.5 label-caps">BOL #</th>
                <th className="text-right px-4 py-2.5 label-caps">Claimed W (t)</th>
                <th className="text-right px-4 py-2.5 label-caps">System W (t)</th>
                <th className="text-right px-4 py-2.5 label-caps">Claimed $</th>
                <th className="text-right px-4 py-2.5 label-caps">System $</th>
                <th className="text-right px-4 py-2.5 label-caps">Delta</th>
                <th className="text-left px-4 py-2.5 label-caps">Reason</th>
              </tr>
            </thead>
            <tbody>
              {inv.reconciliation.map((r, i) => {
                const S = LINE_STATUS_ICON[r.status] || LINE_STATUS_ICON.unmatched;
                const Icon = S.icon;
                return (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`hinv-line-${r.bol_number}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                        <Icon className={`w-3.5 h-3.5 ${S.color}`} />
                        <span className={`uppercase tracking-widest font-bold text-[10px] ${S.color}`}>{S.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.bol_number}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.claimed_weight_tons}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-600">{r.system_weight_tons ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{money(r.claimed_amount)}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-600">{r.system_amount == null ? "—" : money(r.system_amount)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-bold ${r.delta == null ? "text-gray-400" : Math.abs(r.delta) < 0.01 ? "text-emerald-700" : r.delta > 0 ? "text-red-700" : "text-amber-700"}`}>
                      {r.delta == null ? "—" : (r.delta > 0 ? "+" : "") + money(r.delta)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">{r.reasons?.join("; ") || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
